import { Box, Stack, Typography } from '@mui/joy';
import { ReactNode } from 'react';

import { BellNotificationSeverity, BellNotificationSurface } from 'notifications/enums';
import { IBellNotification, IBellNotificationCta } from 'notifications/types';
import { getNotificationContent } from 'notifications/utils/notification';
import { Direction, Level } from 'theme/enums';

import { NotificationActions } from './NotificationActions';
import { NotificationReferences } from './NotificationReferences';
import { NotificationSubtitle } from './NotificationSubtitle';

interface IProps {
  notification: IBellNotification;
  surface: BellNotificationSurface;
  onAction: (notification: IBellNotification, cta: IBellNotificationCta) => void;
  // Inside the title, before it (the drawer row's screen-reader unread label).
  titleStart?: ReactNode;
  // Between the subtitle and the actions (the drawer row's relative time).
  beforeActions?: ReactNode;
}

interface ISurfaceStyle {
  titleLevel: Level;
  subtitleMaxRows: number;
  hasErrorTitle: boolean;
}

const SURFACE_STYLES: Record<BellNotificationSurface, ISurfaceStyle> = {
  [BellNotificationSurface.DRAWER]: {
    titleLevel: Level.TITLE_SM,
    subtitleMaxRows: 2,
    hasErrorTitle: true,
  },
  [BellNotificationSurface.TOAST]: {
    titleLevel: Level.TITLE_MD,
    subtitleMaxRows: 3,
    hasErrorTitle: false,
  },
};

// The title and its end chip: the chip sits next to the title when it fits, else on the next line.
// Gap, not Stack spacing: spacing's margins misplace an item that wraps.
const TITLE_ROW_SX = {
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 0.75,
} as const;

// The handler's content of one notification, shared by the drawer row and the toast card.
export const NotificationContent = ({
  notification,
  surface,
  onAction,
  titleStart,
  beforeActions,
}: IProps) => {
  const { title, titleEnd, detail, references, subtitle, severity } =
    getNotificationContent(notification);
  const { titleLevel, subtitleMaxRows, hasErrorTitle } = SURFACE_STYLES[surface];
  const isError = BellNotificationSeverity.ERROR === severity;

  return (
    <Stack data-testid="notificationContent" sx={{ flex: 1, minWidth: 0, gap: 0.5 }}>
      {/* A div: the title can end in a chip, a block element. */}
      <Typography
        component="div"
        level={titleLevel}
        sx={{
          // Step 600, not plainColor: plainColor is too light on the danger tint in both modes.
          color: hasErrorTitle && isError ? 'danger.600' : 'text.primary',
          fontWeight: 'md',
          overflowWrap: 'anywhere',
        }}
      >
        {titleStart}
        <Stack direction={Direction.ROW} spacing={0} sx={TITLE_ROW_SX}>
          <Box component="span" sx={{ minWidth: 0 }}>
            {title}
          </Box>
          {titleEnd}
        </Stack>
      </Typography>

      {!!detail && <Box>{detail}</Box>}

      {!!references?.length && <NotificationReferences references={references} />}

      {subtitle && (
        <NotificationSubtitle subtitle={subtitle} maxRows={subtitleMaxRows} isError={isError} />
      )}

      {beforeActions}

      <NotificationActions notification={notification} onAction={onAction} surface={surface} />
    </Stack>
  );
};
